import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import { CustomModal } from '../../../components/common/CustomModal'
import { Billing_Cycle_Options } from '../../../constants'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { BillingCycle, useGeneratePreBillsForAllAttorneysMutation } from '../../../../generated'
import { endOfDay } from 'date-fns'
import { useState } from 'react'
import { Alert } from '../../../components/common/Alert'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { PreBillsBulkActionsProps } from '../../../interfaceTypes/props'

interface GenerateAllPreBills {
  billingCycle: BillingCycle
  customDate?: string
}

const PreBillsBulkActions = ({
  openModalToGenerateAllPreBills,
  setOpenModalToGenerateAllPreBills,
  refetch
}: PreBillsBulkActionsProps) => {
  const [generatePreBillsData, setGeneratePreBillsData] = useState<GenerateAllPreBills>({
    billingCycle: BillingCycle.EndOfMonth,
    customDate: endOfDay(new Date()).toISOString()
  })

  const [generatePreBillsForAllAttorneys, { loading: generatingAllPreBills }] =
    useGeneratePreBillsForAllAttorneysMutation({
      onError: error => {
        console.log('error generating all pre bills', error)
        Alert.error(error.message)
      },
      onCompleted: data => {
        const { generatePreBillsForAllAttorneys } = data || {}
        const { billIds, response } = generatePreBillsForAllAttorneys || {}

        if (response?.status === 200) {
          Alert.success(`Generated ${billIds.length} Pre Bills!`)
          refetch()
        }
      }
    })

  const closeGenerateAllPreBillsModal = () => setOpenModalToGenerateAllPreBills(false)

  const handleGeneratePreBillsForAllAttorneys = async () => {
    await generatePreBillsForAllAttorneys({
      variables: {
        generateAllPreBillsInput: {
          ...generatePreBillsData
        }
      }
    })
    closeGenerateAllPreBillsModal()
  }

  const handleCustomDateChange = (date: Date | null) => {
    if (date) setGeneratePreBillsData(prev => ({ ...prev, customDate: endOfDay(new Date(date)).toDateString() }))
    else setGeneratePreBillsData(prev => ({ ...prev, customDate: undefined }))
  }

  return (
    <>
      <CustomModal
        title="Select Billing Cycle to Generate All Pre Bills"
        isOpen={openModalToGenerateAllPreBills}
        handleClose={closeGenerateAllPreBillsModal}
      >
        <Box display={'flex'} alignItems={'center'} mb={3} gap={3}>
          <FormControl sx={{ width: 360 }}>
            <InputLabel>Billing Cycle</InputLabel>
            <Select
              value={generatePreBillsData.billingCycle}
              label="Billing Cycle"
              onChange={e =>
                setGeneratePreBillsData(prev => ({ ...prev, billingCycle: e.target.value as BillingCycle }))
              }
            >
              {Billing_Cycle_Options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {generatePreBillsData.billingCycle === BillingCycle.CustomDate && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Custom Date"
                value={generatePreBillsData.customDate}
                onChange={handleCustomDateChange}
                renderInput={params => <TextField {...params} sx={{ minWidth: 360 }} />}
              />
            </LocalizationProvider>
          )}
        </Box>

        <DialogActions>
          <Button onClick={closeGenerateAllPreBillsModal}>Cancel</Button>
          <Button
            variant="contained"
            type="submit"
            disabled={generatingAllPreBills}
            endIcon={generatingAllPreBills && <CircularProgress size={20} color="inherit" />}
            onClick={handleGeneratePreBillsForAllAttorneys}
          >
            Generate All Pre Bills
          </Button>
        </DialogActions>
      </CustomModal>
    </>
  )
}

export default PreBillsBulkActions
